/**
 * ScheduleSlot Types for MEV-Med Frontend
 *
 * Type definitions for schedule slots management
 *
 * @version 2025.1.0
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Slot status enum
 */
export enum SlotStatus {
  DISPONIVEL = 'Disponível',
  PARCIALMENTE_OCUPADO = 'Parcialmente Ocupado',
  TOTALMENTE_OCUPADO = 'Totalmente Ocupado',
  BLOQUEADO = 'Bloqueado',
  CANCELADO = 'Cancelado',
}

/**
 * Appointment type enum for slots
 */
export enum SlotAppointmentType {
  CONSULTA = 'Consulta',
  EXAME = 'Exame',
  RETORNO = 'Retorno',
  URGENCIA = 'Urgência',
  PREVENTIVA = 'Preventiva',
}

/**
 * Recurring pattern enum
 */
export enum RecurringPattern {
  SEMANAL = 'Semanal',
  QUINZENAL = 'Quinzenal',
  MENSAL = 'Mensal',
  PERSONALIZADO = 'Personalizado',
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Company reference in slot
 */
export interface SlotCompany {
  id: number;
  tradeName?: string;
  legalName?: string;
}

/**
 * Main ScheduleSlot interface
 */
export interface ScheduleSlot {
  id: number;
  doctorName: string;
  location: string;
  slotDate: string;
  startTime: string;
  endTime: string;
  duration: number;
  status: SlotStatus;
  appointmentType: SlotAppointmentType;
  maxCapacity: number;
  currentBookings: number;
  remainingCapacity: number;
  occupancyPercentage: number;
  isRecurring: boolean;
  recurringPattern?: RecurringPattern;
  notes?: string;
  isActive: boolean;
  isAvailable: boolean;
  companyId: number;
  company?: SlotCompany;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create schedule slot request
 */
export interface CreateScheduleSlotRequest {
  doctorName: string;
  location: string;
  slotDate: string;
  startTime: string;
  endTime: string;
  duration: number;
  appointmentType: SlotAppointmentType;
  maxCapacity: number;
  isRecurring?: boolean;
  recurringPattern?: RecurringPattern;
  notes?: string;
  companyId: number;
}

/**
 * Update schedule slot request
 */
export interface UpdateScheduleSlotRequest {
  doctorName?: string;
  location?: string;
  slotDate?: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
  appointmentType?: SlotAppointmentType;
  maxCapacity?: number;
  isRecurring?: boolean;
  recurringPattern?: RecurringPattern;
  notes?: string;
}

/**
 * Form data for schedule slot
 */
export interface ScheduleSlotFormData {
  doctorName: string;
  location: string;
  slotDate: string;
  startTime: string;
  endTime: string;
  duration: number;
  appointmentType: SlotAppointmentType;
  maxCapacity: number;
  isRecurring: boolean;
  recurringPattern: RecurringPattern | '';
  notes: string;
  companyId: number | '';
}

/**
 * Schedule slot filters
 */
export interface ScheduleSlotFilters {
  search?: string;
  companyId?: number;
  clinicId?: number;
  doctorName?: string;
  location?: string;
  status?: SlotStatus;
  appointmentType?: SlotAppointmentType;
  startDate?: string;
  endDate?: string;
  isRecurring?: boolean;
  isActive?: boolean;
}

/**
 * Pagination options
 */
export interface ScheduleSlotListParams extends ScheduleSlotFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Paginated response
 */
export interface ScheduleSlotListResponse {
  scheduleSlots: ScheduleSlot[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Occupancy statistics
 */
export interface OccupancyStats {
  totalSlots: number;
  availableSlots: number;
  bookedSlots: number;
  blockedSlots: number;
  occupancyRate: number;
  averageBookingsPerSlot: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Status labels for display
 */
export const SLOT_STATUS_LABELS: Record<SlotStatus, string> = {
  [SlotStatus.DISPONIVEL]: 'Disponível',
  [SlotStatus.PARCIALMENTE_OCUPADO]: 'Parcialmente Ocupado',
  [SlotStatus.TOTALMENTE_OCUPADO]: 'Lotado',
  [SlotStatus.BLOQUEADO]: 'Bloqueado',
  [SlotStatus.CANCELADO]: 'Cancelado',
};

/**
 * Status colors for chips/badges
 */
export const SLOT_STATUS_COLORS: Record<SlotStatus, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  [SlotStatus.DISPONIVEL]: 'success',
  [SlotStatus.PARCIALMENTE_OCUPADO]: 'warning',
  [SlotStatus.TOTALMENTE_OCUPADO]: 'error',
  [SlotStatus.BLOQUEADO]: 'default',
  [SlotStatus.CANCELADO]: 'error',
};

/**
 * Appointment type labels
 */
export const SLOT_APPOINTMENT_TYPE_LABELS: Record<SlotAppointmentType, string> = {
  [SlotAppointmentType.CONSULTA]: 'Consulta',
  [SlotAppointmentType.EXAME]: 'Exame',
  [SlotAppointmentType.RETORNO]: 'Retorno',
  [SlotAppointmentType.URGENCIA]: 'Urgência',
  [SlotAppointmentType.PREVENTIVA]: 'Preventiva',
};

/**
 * Recurring pattern labels
 */
export const RECURRING_PATTERN_LABELS: Record<RecurringPattern, string> = {
  [RecurringPattern.SEMANAL]: 'Semanal',
  [RecurringPattern.QUINZENAL]: 'Quinzenal',
  [RecurringPattern.MENSAL]: 'Mensal',
  [RecurringPattern.PERSONALIZADO]: 'Personalizado',
};

/**
 * Default form values
 */
export const DEFAULT_SCHEDULE_SLOT_FORM: ScheduleSlotFormData = {
  doctorName: '',
  location: '',
  slotDate: '',
  startTime: '08:00',
  endTime: '09:00',
  duration: 60,
  appointmentType: SlotAppointmentType.CONSULTA,
  maxCapacity: 1,
  isRecurring: false,
  recurringPattern: '',
  notes: '',
  companyId: '',
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format time range for display
 */
export function formatTimeRange(startTime: string, endTime: string): string {
  return `${startTime} - ${endTime}`;
}

/**
 * Format capacity for display
 */
export function formatCapacity(current: number, max: number): string {
  return `${current}/${max}`;
}

/**
 * Calculate duration in minutes from time range
 */
export function calculateDuration(startTime: string, endTime: string): number {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  const startMinutes = (startHour ?? 0) * 60 + (startMin ?? 0);
  const endMinutes = (endHour ?? 0) * 60 + (endMin ?? 0);

  return endMinutes - startMinutes;
}

/**
 * Check if slot is available for booking
 */
export function isSlotAvailable(slot: ScheduleSlot): boolean {
  const availableStatuses = [SlotStatus.DISPONIVEL, SlotStatus.PARCIALMENTE_OCUPADO];
  return (
    slot.isActive &&
    availableStatuses.includes(slot.status) &&
    slot.currentBookings < slot.maxCapacity
  );
}

/**
 * Get status from bookings and capacity
 */
export function getStatusFromBookings(current: number, max: number, isBlocked: boolean = false): SlotStatus {
  if (isBlocked) return SlotStatus.BLOQUEADO;
  if (current === 0) return SlotStatus.DISPONIVEL;
  if (current >= max) return SlotStatus.TOTALMENTE_OCUPADO;
  return SlotStatus.PARCIALMENTE_OCUPADO;
}
